import styled from '@emotion/styled';
import {
  formatTimeDuration,
  timeDurationToMilliseconds,
} from '@racemap/utilities/formattings/times';
import { isNoBoolean } from '@racemap/utilities/functions/utils';
import { isDefined, isNotNull } from '@racemap/utilities/functions/validation';
import { Button, Flex, Segmented, Space } from 'antd';
import type { Immutable } from 'immer';
import { isArray } from 'lodash';
import React, {
  type CSSProperties,
  type ComponentType,
  type FocusEventHandler,
  type InputHTMLAttributes,
  useEffect,
  useState,
} from 'react';
import {
  type FormCheckProps as BSFormCheckProps,
  FormCheck as BtFormCheck,
  FormControl,
  FormLabel,
  InputGroup,
} from 'react-bootstrap';
import {
  type GroupBase,
  type InputProps,
  type Options,
  type ReactSelectProps,
  Select,
  type SingleValue,
  type StylesConfig,
  components,
} from '../components/BasicComponents/Select';
import { toastError } from './utils/Utils';

function createLabel(label?: React.ReactNode, required?: boolean, hint?: React.ReactNode) {
  if (label == null) return <></>;
  switch (typeof label) {
    case 'string':
    case 'boolean':
    case 'number':
      return (
        <FormLabel>
          <Space size="small">
            <span>
              {label} {required && <sup>*</sup>}
            </span>
            {hint}
          </Space>
        </FormLabel>
      );
    case 'object':
      return (
        <>
          {label}
          {hint}
        </>
      );
    default:
      return <></>;
  }
}

// workaround, in the popover links seems to get blocket. that component takes the same props,
// but trigger the location change with javascript
export function PopoverLink({
  href,
  target = '',
  rel = '',
  children,
}: {
  href: string;
  target?: string;
  rel?: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} target={target} rel={rel}>
      {children}
    </a>
  );
}

type FormGroupProps = React.HTMLProps<HTMLDivElement>;

export function FormGroup({ children, ...props }: FormGroupProps) {
  return (
    <div className="form-group" {...props}>
      {children}
    </div>
  );
}

export interface TextInputProps<T>
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'value'> {
  value?: T;
  hint?: React.ReactNode;
  label?: React.ReactNode;
  onChange?: (arg0: T, arg1?: boolean) => void;
  onDetailsChange?: (arg0: { value: T; name: string }, arg1?: boolean) => void;
  groupStyle?: CSSProperties;
  size?: 'sm' | 'lg';
  unit?: string;
  rows?: number;
}

export function FormInput<T extends string | number | Array<string> | undefined = string>({
  type = 'text',
  hint,
  label,
  onChange = noop,
  onDetailsChange = noop,
  required = false,
  disabled = false,
  className = '',
  style = {},
  groupStyle = {},
  unit,
  ...props
}: TextInputProps<T>) {
  return (
    <FormGroup style={groupStyle}>
      <Flex gap={5} align="center">
        {createLabel(label, required, hint)}
      </Flex>
      <InputGroup>
        <FormControl
          className={className}
          type={type}
          onChange={({ target }) => {
            const {
              validity: { valid },
              value,
              name,
            } = target;
            if (value != null) onChange(value as unknown as T, valid);
            if (value != null) onDetailsChange({ value: value as unknown as T, name }, valid);
          }}
          required={required}
          disabled={disabled}
          style={style}
          {...props}
        />
        {unit && (
          <InputGroup.Append>
            <InputGroup.Text>{unit}</InputGroup.Text>
          </InputGroup.Append>
        )}
      </InputGroup>
    </FormGroup>
  );
}

export function FormNumberInput(props: TextInputProps<string>): React.ReactElement {
  return (
    <FormInput
      type="number"
      {...props}
      value={props.value?.toString() || ''}
      onChange={(newValue: string, valid) => {
        const { pattern } = props;
        const isPatternCheckValid = pattern == null || new RegExp(pattern).test(newValue);
        props.onChange?.(newValue, isPatternCheckValid && valid);
      }}
    />
  );
}

type TextInputWithButtonProps<T> = TextInputProps<T> & {
  buttonLabel: React.ReactNode;
  onButtonClick: () => void;
};

export function FormInputWithButton({
  type = 'text',
  label,
  buttonLabel,
  onButtonClick,
  onChange = noop,
  required = false,
  disabled = false,
  className = '',
  ...props
}: TextInputWithButtonProps<string>) {
  return (
    <FormGroup>
      {createLabel(label)}
      <InputGroup>
        <FormControl
          className={className}
          type={type}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          disabled={disabled}
          {...props}
        />
        <InputGroup.Append>
          <Button onClick={onButtonClick}>{buttonLabel}</Button>
        </InputGroup.Append>
      </InputGroup>
    </FormGroup>
  );
}

export function FormTextarea({
  hint,
  label,
  value = '',
  onChange = noop,
  required = false,
  groupStyle = {},
  maxLength,
  ...props
}: TextInputProps<string>) {
  const [letterCount, setLetterCount] = useState(value.length);

  return (
    <FormGroup style={groupStyle}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {createLabel(label, required, hint)}
      </div>
      <textarea
        className="form-control"
        onChange={(event) => {
          setLetterCount(event.target.value.length);
          onChange(event.target.value);
        }}
        required={required}
        value={value}
        maxLength={maxLength}
        {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
      {maxLength && (
        <span style={{ fontSize: '80%' }}>{maxLength - letterCount} characters left</span>
      )}
    </FormGroup>
  );
}

export function FormInputWithAddon({
  type = 'text',
  iconComponent: IconComponent,
  onChange = noop,
  required = false,
  ...props
}: TextInputProps<string> & {
  iconComponent: ComponentType;
}) {
  return (
    <FormGroup>
      <InputGroup>
        <InputGroup.Prepend>
          <InputGroup.Text>
            <IconComponent />
          </InputGroup.Text>
        </InputGroup.Prepend>
        <FormControl
          type={type}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          {...props}
        />
      </InputGroup>
    </FormGroup>
  );
}

export function FormInputTime({
  value,
  onChange,
  ...props
}: {
  value: number;
  onChange: (arg0: number) => void;
}) {
  const formInput = (
    <BlurFormInput
      type="text"
      value={value != null && value === -1 ? '-1' : value != null ? formatTimeDuration(value) : ''}
      step={1}
      onChange={(newValue) =>
        newValue !== '' && newValue === '-1'
          ? onChange(-1)
          : newValue !== '' && Number.isNaN(timeDurationToMilliseconds(newValue))
            ? toastError('The format for the manual result have to be hh:mm:ss.')
            : onChange(timeDurationToMilliseconds(newValue))
      }
      {...props}
    />
  );

  return formInput;
}

type SelectOption = { value: unknown; label?: string };

const getSelectCustomStyles = <Option extends SelectOption, IsMulti extends boolean>(
  isMulti: boolean,
) => {
  const styles: StylesConfig<Option, IsMulti> = {
    container: (provided) => ({
      ...provided,
      width: '100%',
      padding: 0,
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      height: isMulti ? undefined : 33.5,
    }),
    control: (provided, state) => {
      const out = {
        ...provided,
        minHeight: '33.5px',
        alignItems: 'center',
        borderColor: state.isFocused ? '#7eb1d2' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgb(54 115 154 / 25%)' : undefined,
        ':hover': {
          borderColor: state.isFocused ? '#7eb1d2' : '#ced4da',
        },
      };

      return out;
    },
    menuList: (provided) => ({ ...provided }),
    menu: (provided) => ({ ...provided, zIndex: 3 }),
    option: (provided) => ({
      ...provided,
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '1px',
    }),
  };

  return styles;
};

interface SelectProps<T extends string | null> extends Omit<ReactSelectProps, 'onChange'> {
  hint?: string | React.ReactNode;
  label?: string | React.ReactNode;
  onChange?: (arg0: T) => void;
  value?: T;
  children: Array<React.ReactElement<HTMLOptionElement> | null | undefined | boolean>;
  isClearable?: boolean;
  containerStyle?: CSSProperties;
  required?: boolean;
}

interface SelectOptionType {
  readonly label: string;
  readonly value: string;
}

// TODO: Replace with ant design select component
export const FormSelect = <T extends string | null = string | null>({
  children,
  hint,
  label,
  value,
  required,
  onChange = noop,
  id,
  containerStyle = {},
  ...props
}: SelectProps<T>) => {
  const validChildren = children
    .filter(isDefined)
    .filter(isNotNull)
    .filter(isNoBoolean)
    .filter((c) => c.props != null);
  let options: Options<SelectOptionType> = [];
  if (isArray(children)) {
    options = validChildren.map((c) => ({
      value: c.props.value as string,
      label: String(c.props.children),
    }));
  }
  const choosedValue = options.find((o: SelectOptionType) => o.value === value);

  return (
    <div style={{ width: '100%', marginBottom: '1rem', ...containerStyle }}>
      <Flex gap={5}>{createLabel(label, required, hint)}</Flex>
      <Select<SelectOptionType, false, GroupBase<SelectOptionType>>
        {...(props as Omit<
          ReactSelectProps<SelectOptionType, false, GroupBase<SelectOptionType>>,
          'onChange'
        >)}
        onChange={(choosedOption) => onChange((choosedOption?.value || null) as T)}
        options={options}
        value={choosedValue}
        styles={getSelectCustomStyles(false)}
        classNamePrefix="list"
        required={required}
        inputId={id}
        components={{ Input }}
      />
    </div>
  );
};

const Input = (props: InputProps<SelectOptionType, false, GroupBase<SelectOptionType>>) => {
  const value = props.selectProps.value as SingleValue<SelectOptionType>;

  return (
    <components.Input
      {...props}
      required={props.selectProps.required}
      value={value?.label || props.selectProps.inputValue}
    />
  );
};

interface MultiSelectProps extends Omit<ReactSelectProps, 'onChange'> {
  label?: string;
  required?: boolean;
  children: Array<React.ReactElement<HTMLOptionElement>>;
  value: Array<string> | Immutable<Array<string>>;
  onChange: (newValues: Array<string>) => void;
  hint?: React.ReactNode;
  containerStyle?: CSSProperties;
}

export function FormMultiSelect({
  value,
  label,
  onChange = noop,
  closeMenuOnSelect = false,
  required = false,
  children,
  hint,
  containerStyle = {},
  ...props
}: MultiSelectProps) {
  const options = children
    .filter((c) => c != null)
    .map((c) => ({
      value: c.props.value,
      label: String(c.props.children),
    }));
  const choosedValues = options.filter((o) => value?.includes(o.value));

  return (
    <div style={{ width: '100%', marginBottom: '1rem', ...containerStyle }}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {createLabel(label, required, hint)}
      </div>
      <Select
        isClearable
        isMulti
        closeMenuOnSelect={closeMenuOnSelect}
        options={options}
        className="basic-multi-select"
        classNamePrefix="select"
        onChange={(newValue) =>
          onChange(
            newValue != null ? (newValue as Array<SelectOption>).map((i) => String(i.value)) : [],
          )
        }
        value={choosedValues}
        styles={getSelectCustomStyles(true) as StylesConfig<unknown, boolean, GroupBase<unknown>>}
        {...props}
      />
    </div>
  );
}

interface FormCheckProps extends Omit<BSFormCheckProps, 'onChange' | 'value' | 'label'> {
  onChange?: (checked: boolean) => void;
  value: boolean | undefined;
  required?: boolean;
  label?: React.ReactNode | string;
  labelstyle?: CSSProperties;
  hint?: React.ReactNode;
}

export function FormCheck({
  onChange = noop,
  value,
  required = false,
  label,
  labelstyle,
  hint,
  ...props
}: FormCheckProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(!!event.target.checked);
  };
  const labelWithRequiredFlag = (
    <div style={labelstyle}>
      <Space size={3}>
        {label}
        {required ? '*' : ''}
        {hint}
      </Space>
    </div>
  );

  return (
    <div>
      <BtFormCheck
        style={{ cursor: 'pointer', marginRight: '2px' }}
        type={props.type || 'switch'}
        label={labelWithRequiredFlag}
        checked={value ?? false}
        onChange={handleChange}
        required={required}
        {...props}
      />
    </div>
  );
}

export interface BlurFormInputProps<T> extends TextInputProps<T> {
  onChange?: (newValue: T, valid?: boolean) => void;
  value?: T;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  children: React.ReactElement;
}

export const BlurFormInputWrapper = <T,>({
  children,
  onChange,
  value,
  ...rest
}: BlurFormInputProps<T>) => {
  const [intermediateValue, setIntermediateValue] = useState<T | undefined>(value);
  const [isValid, setValidity] = useState<boolean>(true);

  const handleValueChange = (newValue?: T, valid = true) => {
    setIntermediateValue(newValue);
    setValidity(valid);
  };

  useEffect(() => {
    handleValueChange(value, true);
  }, [value]);

  return (
    <>
      {React.cloneElement(children, {
        ...rest,
        onChange: (newValue: T, valid = true) => {
          handleValueChange(newValue, valid);
        },
        onBlur: () => {
          if (!isValid) {
            setIntermediateValue(value);
            return toastError('Your input is invalid. Please change!');
          }
          if (onChange != null && intermediateValue != null) {
            onChange(intermediateValue, isValid);
          }
        },
        value: intermediateValue,
      })}
    </>
  );
};

export const BlurFormInput = <T extends string | number | null | undefined = string>(
  props: TextInputProps<T>,
) => (
  <BlurFormInputWrapper<T> {...props}>
    <FormInput />
  </BlurFormInputWrapper>
);

export const BlurFormTextarea = <_T extends string | null | undefined = string>(
  props: TextInputProps<string>,
) => (
  <BlurFormInputWrapper<string> {...props}>
    <FormTextarea />
  </BlurFormInputWrapper>
);

export const BlurFormNumberInput = <_T extends string | null | undefined = string>(
  props: TextInputProps<string>,
) => (
  <BlurFormInputWrapper<string> {...props}>
    <FormNumberInput />
  </BlurFormInputWrapper>
);

export function FormInputUrl(props: TextInputProps<string>) {
  const { onChange = noop, ...rest } = props;

  function makeUrl(value: string) {
    if (value) {
      if (value.match(/^https?:\/\//)) {
        return value;
      }
      return `http://${value}`;
    }
    return value;
  }

  return <BlurFormInput onChange={(newValue: string) => onChange(makeUrl(newValue))} {...rest} />;
}

export const FullWidthSegmented = styled(Segmented)`
  &.ant-segmented.ant-segmented-block .ant-segmented-item {
    min-width: 0;
    margin-bottom: 0;
  }
`;

function noop() {}
